/**
 * Drawer
 */

// Import extend function.
import Component from '../../js/core/component.js';
import getSiblings from '../../js/utilities/get-siblings/u-get-siblings.js';
import {getFocusableElement, getFocusableElements} from '../../js/utilities/get-focusable-element/u-get-focusable-element.js';
import {detachWithPlaceholder, attachToPlaceholder} from '../../js/utilities/detach-with-placeholder/u-detach-with-placeholder.js';

class Drawer extends Component {
	/**
	 * Default settings for this component
	 * @return {object} Default settings
	 */
	_defaultSettings () {
		return {
			debug: false,
			classes: {
				isClosed: 'is-closed',
				isOpen: 'is-open',
				wrapper: 'c-drawer',
				content: 'c-drawer__content',
				bodyWhenOpen: 'c-drawer-is-open-fixed'
			},
			on: {
				open: () => {},
				close: () => {},
				toggle: () => {}
			}
		};
	}

	/**
	 * Initialize Drawer
	 */
	_init () {
		// Create main element
		this.element = document.createElement('div');
		this.element.classList.add(this.settings.classes.wrapper);

		this.content = document.createElement('div');
		this.content.classList.add(this.settings.classes.content);

		this.content.style.paddingTop = document.body.style.paddingTop;

		this.element.appendChild(this.content);

		this.siteHeader = document.querySelector('.c-header');

		// Assign our keypresshandler-function to a property
		this.keyPressHandler = this._keyPress.bind(this);

		// Append main element to body
		document.body.appendChild(this.element);
	}

	/**
	 * Hide sibling elements from screen readers
	 */
	_hideSiblings () {
		var header = document.querySelector('.c-header');
		var exclude = [header];
		getSiblings(this.element).forEach(target => {
			if (exclude.indexOf(target) !== -1) {
				return;
			}
			// Save the original value if the element already has an aria-hidden attribute
			const original = target.getAttribute('aria-hidden');
			if (original) {
				target.setAttribute('data-aria-hidden-original', original);
			}
			target.setAttribute('aria-hidden', 'true');
		});
	}

	/**
	 * Restore the aria-hidden attribute of sibling elements
	 */
	_restoreSiblings () {
		getSiblings(this.element).forEach(target => {
			const original = target.getAttribute('data-aria-hidden-original');
			if (original) {
				target.setAttribute('aria-hidden', original);
				target.removeAttribute('data-aria-hidden-original');
			}
			else {
				target.removeAttribute('aria-hidden');
			}
		});
	}

	/**
	 * Keypress event handler
	 */
	_keyPress (event) {
		if (!this.isOpen()) {
			return;
		}

		if (event.which === 9) {
			this._trapFocus(event);
		}
	}

	/**
	 * Trap focus when using tab / shift+tab
	 * @param  {event} event
	 */
	_trapFocus (event) {
		var focusableElements = getFocusableElements(this.element);

		// If the focus is at the first element and we want to focus back, set focus to the last element
		if (event.shiftKey && document.activeElement === focusableElements[0]) {
			focusableElements[focusableElements.length - 1].focus();
			event.preventDefault();
		}
		// If the focus is at the last element and we want to focus forward, set focus to the first element
		else if (!event.shiftKey && document.activeElement === focusableElements[focusableElements.length - 1]) {
			focusableElements[0].focus();
			event.preventDefault();
		}
		// If the dialog dosen't have any focusable element, just prevent tabbing
		else if (focusableElements.length < 1) {
			event.preventDefault();
		}
	}

	/* =============================================================================
	 * Public methods
	============================================================================= */

	/**
	 * Open drawer
	 */
	open () {
		detachWithPlaceholder(this.siteHeader);
		this.element.insertBefore(this.siteHeader, this.content);

		// Toggle hidden/visible classes
		this.element.classList.remove(this.settings.classes.isClosed);
		this.element.classList.add(this.settings.classes.isOpen);

		// Add class to body to indicate that the drawer is open
		document.body.classList.add(this.settings.classes.bodyWhenOpen);

		this._hideSiblings();

		var firstElement = getFocusableElement(this.element);
		if (firstElement) {
			firstElement.focus();
		}

		// Make sure we are at the top
		this.element.scrollTop = 0;

		// Trap focus
		document.addEventListener('keydown', this.keyPressHandler, true);

		this.settings.on.open(this);
	}

	/**
	 * Close drawer
	 */
	close () {
		attachToPlaceholder(this.siteHeader);

		// Toggle hidden/visible classes
		this.element.classList.remove(this.settings.classes.isOpen);
		this.element.classList.add(this.settings.classes.isClosed);

		// Remove class to body to indicate that the drawer is hidden
		document.body.classList.remove(this.settings.classes.bodyWhenOpen);

		this._restoreSiblings();

		// Remove the focus trap
		document.removeEventListener('keydown', this.keyPressHandler, true);

		this.settings.on.close(this);
	}

	/**
	 * Toggle drawer
	 */
	toggle () {
		// If the drawer is open, hide drawer
		if (this.isOpen()) {
			this.close();
		}
		// Otherwise, show drawer
		else {
			this.open();
		}

		this.settings.on.toggle(this);
	}

	/**
	 * Check if the drawer is open
	 */
	isOpen () {
		// Check if main element contains "is visible"-class
		return this.element.classList.contains(this.settings.classes.isOpen);
	}

}

export default Drawer;


// WEBPACK FOOTER //
// ./components/drawer/c-drawer.js